import { useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, Select } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import type { Person } from '../types';

export interface AssignDialogProps {
  ticketLabel: string;
  assignees: Person[];
  /** Preselected person: the category's default assignee from the banner. */
  defaultId: string | null;
  onAssign: (assigneeId: string) => Promise<void>;
  onClose: () => void;
}

/** „Zuweisen" from the assignment banner of Backoffice 05 (no frame; own texts): pick a person with tickets access. */
export function AssignDialog({ ticketLabel, assignees, defaultId, onAssign, onClose }: AssignDialogProps) {
  const formId = useId();
  const [assigneeId, setAssigneeId] = useState(defaultId ?? assignees[0]?.id ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!assigneeId) return;
    setPending(true);
    setError(null);
    try {
      await onAssign(assigneeId);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={480}
      eyebrow={ticketLabel}
      title="Ticket zuweisen"
      subtitle="Die Person bekommt eine Benachrichtigung und das Ticket erscheint mit ihrem Kürzel auf dem Board."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={!assigneeId}>
            Zuweisen
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void submit(event)}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <Select
          label="Zuständig"
          value={assigneeId}
          onChange={(event) => setAssigneeId(event.target.value)}
          options={assignees.map((person) => ({ value: person.id, label: person.name }))}
        />
      </form>
    </Dialog>
  );
}
